// ─── Mode daftar kombinasi (B1 "daftar tertutup") ────────────────────────────
//
// SATU PINTU untuk pertanyaan "kombinasi yang TIDAK punya baris
// `product_variants` — dijual atau tidak?" dan untuk semua keputusan yang
// menurunkannya (muat form, simpan form, picker storefront, picker admin).
//
// KENAPA ADA: sampai B1 jawabannya DIHITUNG dari jumlah kombinasi
// (`absentComboSellable` di lazyVariants.ts: preorder > 200 & bukan PO
// terbatas = dijual, lahir saat dipesan). Hitungan itu berbalik setiap kali
// pohon opsi melintasi 200, dan memaksa form menulis baris NONAKTIF untuk
// setiap kombinasi yang tak dijual — 221 rb baris nonaktif di PROD, 6 yang
// pernah dipesan (audit `docs/audit-ledakan-varian-2026-11.md`).
//
// BENTUKNYA: kolom `products.absent_combos_sellable`
//   · `true`  = TERBUKA — kombinasi tanpa baris dijual, barisnya lahir saat
//               dipesan (`resolve_or_create_preorder_variant`);
//   · `false` = TERTUTUP — kombinasi tanpa baris TIDAK dijual; database
//               menolak melahirkan barisnya (penjaga terakhir semua layar);
//   · `null`  = belum ditandai → aturan HARI INI (`absentComboSellable`).
//
// SAKLAR PER TOKO (putusan pemilik 2026-11-01): kolom itu hanya boleh terisi
// di toko yang saklarnya menyala (`stores.combo_mode_enabled`) — trigger DB
// memaksanya NULL di toko lain. Jadi saklar mati = setiap pembaca di modul ini
// jatuh ke cabang `null` = perilaku hari ini persis, tanpa pembaca perlu tahu
// soal saklarnya. Yang perlu tahu hanya PENULIS (form, impor): mereka memutuskan
// mode baru hanya bila saklar menyala.
//
// Mode berganti SATU ARAH (terbuka → tertutup). Tertutup → terbuka akan
// menjual seketika setiap kombinasi yang tak pernah dicentang.

import type { ComboRebuildReason } from "@/lib/variantPricing";
import {
  LAZY_VARIANT_THRESHOLD,
  absentComboSellable,
  buildBlockedComboSet,
  comboBlockKey,
  deriveComboCount,
  variantPickerIsLazy,
  type BlockedComboKeys,
  type BlockedCombos,
} from "@/lib/lazyVariants";

export type ComboModeProductType = "preorder" | "ready_stock";

/** Potongan produk yang dibutuhkan setiap keputusan di modul ini. */
export type ComboModeProduct = {
  productType?: string | null;
  poLimited?: boolean | null;
  /** `products.absent_combos_sellable`; null/undefined = belum ditandai. */
  absentCombosSellable?: boolean | null;
};

function productTypeOf(p: ComboModeProduct): ComboModeProductType {
  return p.productType === "ready_stock" ? "ready_stock" : "preorder";
}

function poLimitedOf(p: ComboModeProduct): boolean {
  return productTypeOf(p) === "preorder" && p.poLimited === true;
}

/** Produk ini ditandai TERTUTUP secara eksplisit? */
export function isComboListClosed(p: ComboModeProduct): boolean {
  return p.absentCombosSellable === false;
}

/** Produk ini ditandai TERBUKA secara eksplisit? */
export function isComboListOpen(p: ComboModeProduct): boolean {
  return p.absentCombosSellable === true;
}

/**
 * SATU PINTU: kombinasi yang TIDAK punya baris — dijual?
 *
 * Tanda eksplisit menang; tanpa tanda = aturan hari ini (dihitung dari jumlah
 * kombinasi). Ready stock tak pernah: barisnya yang memegang stok. PO terbatas
 * tak pernah: resolver menolak melahirkan baris (mig 3970/5830).
 */
export function rowlessComboSellable(
  p: ComboModeProduct & { comboCount: number }
): boolean {
  const productType = productTypeOf(p);
  const poLimited = poLimitedOf(p);
  if (p.absentCombosSellable === false) return false;
  if (p.absentCombosSellable === true) return productType === "preorder" && !poLimited;
  return absentComboSellable({ productType, comboCount: p.comboCount, poLimited });
}

/**
 * Aturan backfill = aturan hari ini, dalam bentuk nilai kolom. Cermin SQL
 * `product_combo_mode_today()` di migrasi `varian_daftar_tertutup` — yang
 * dibuktikan "nol beda" di kaki migrasi.
 */
export function comboModeToday(
  p: Omit<ComboModeProduct, "absentCombosSellable"> & { comboCount: number }
): boolean {
  return absentComboSellable({
    productType: productTypeOf(p),
    comboCount: p.comboCount,
    poLimited: poLimitedOf(p),
  });
}

/**
 * Mode yang DISIMPAN sesudah Simpan ini (saklar toko menyala).
 *
 * Tertutup bila: sudah tertutup (satu arah), ready stock, PO terbatas, tanpa
 * kombinasi, atau yang dicentang KURANG DARI SEPARUH kombinasi (putusan
 * pemilik B1). Selain itu: tetap terbuka bila sudah terbuka; produk tanpa
 * tanda (baru / belum di-backfill) mengikuti aturan hari ini — preorder > 200
 * terbuka, ≤ 200 tertutup.
 *
 * `current` = mode sekarang. Untuk DUPLIKAT isi dengan mode produk SUMBER:
 * tanda mode ikut tersalin, dan sumber tertutup melahirkan salinan tertutup.
 */
export function nextComboListMode(input: {
  current: boolean | null | undefined;
  productType?: string | null;
  poLimited?: boolean | null;
  /** Kombinasi tercentang "dijual" di papan form. */
  checked: number;
  /** Seluruh kombinasi kartesius. */
  cartesian: number;
}): boolean {
  if (input.current === false) return false;
  const p = { productType: input.productType, poLimited: input.poLimited };
  if (productTypeOf(p) !== "preorder") return false;
  if (poLimitedOf(p)) return false;
  if (input.cartesian <= 0) return false;
  if (input.checked * 2 < input.cartesian) return false;
  if (input.current === true) return true;
  return input.cartesian > LAZY_VARIANT_THRESHOLD;
}

/**
 * Penyaring INSERT rencana simpan. Hanya menyentuh baris yang BELUM ada di DB
 * (`planVariantSave` menyaring `inserts` final) — baris lama tak pernah dibuang.
 *
 *   · saklar mati       → persis hari ini (`skipInertInsertsOnSave`).
 *   · TERTUTUP          → kombinasi tak dicentang yang belum punya baris TIDAK
 *                          ditulis (di produk tertutup, tanpa baris = tak dijual);
 *                          kombinasi tercentang SELALU ditulis, inert atau tidak.
 *   · TERBUKA           → perilaku hari ini: tak dicentang ditulis nonaktif,
 *                          kombinasi inert dilewati hanya preorder > 200.
 */
export function comboSaveFilters(input: {
  switchOn: boolean;
  /** Mode sesudah simpan (`nextComboListMode`); diabaikan bila saklar mati. */
  target: boolean;
  productType?: string | null;
  poLimited?: boolean | null;
  comboCount: number;
}): { skipInertInserts: boolean; skipUncheckedInserts: boolean } {
  const p = { productType: input.productType, poLimited: input.poLimited };
  const today = absentComboSellable({
    productType: productTypeOf(p),
    comboCount: input.comboCount,
    poLimited: poLimitedOf(p),
  });
  if (!input.switchOn) return { skipInertInserts: today, skipUncheckedInserts: false };
  if (!input.target) return { skipInertInserts: false, skipUncheckedInserts: true };
  return { skipInertInserts: today, skipUncheckedInserts: false };
}

/**
 * KAPAN tanda ditulis (aturan 5: baris dulu, tanda PALING AKHIR).
 *
 * `insertValue` — nilai kolom di payload INSERT produk baru (undefined = kolom
 * tak dikirim). `finalValue` — UPDATE sesudah seluruh baris & struktur
 * tersimpan (undefined = tak perlu).
 *
 *   · Produk baru TERTUTUP lahir tertutup: sebelum barisnya tertulis tak ada
 *     yang dijual — keadaan antara paling aman.
 *   · Produk baru TERBUKA lahir tanpa tanda (= aturan hari ini, sama dengan
 *     sebelum B1) lalu ditandai terbuka di akhir.
 *   · Terbuka → tertutup: baris aktif untuk yang dijual-karena-kosong ditulis
 *     dulu, tanda diganti terakhir. Di setiap keadaan antara, yang dijual ⊆
 *     (yang dijual sebelum) ∪ (yang dijual sesudah).
 */
export function comboModeWritePlan(input: {
  switchOn: boolean;
  isNew: boolean;
  current: boolean | null | undefined;
  target: boolean;
}): { insertValue: boolean | null | undefined; finalValue: boolean | undefined } {
  if (!input.switchOn) return { insertValue: undefined, finalValue: undefined };
  if (input.current === false && input.target === true) {
    throw new Error("Mode kombinasi tertutup tidak bisa dibuka lagi.");
  }
  if (input.isNew) {
    return input.target
      ? { insertValue: null, finalValue: true }
      : { insertValue: false, finalValue: undefined };
  }
  if (input.current === input.target) return { insertValue: undefined, finalValue: undefined };
  return { insertValue: undefined, finalValue: input.target };
}

/**
 * Centang "dijual" untuk kombinasi yang BELUM punya baris saat MEMUAT form —
 * sebagai predikat per kombinasi.
 *
 * Tanpa tanda: persis `absentActiveOnLoad` hari ini (`structure`/`empty` →
 * dicentang; `unmaterialized` → aturan 200). Bertanda:
 *   · TERTUTUP + `unmaterialized`/`empty` → TIDAK dicentang. Ini kelas bug
 *     15 Okt: kombinasi lama tanpa baris di produk tertutup memang tak dijual,
 *     mencentangnya membuat Simpan berikutnya menjualnya.
 *   · TERTUTUP + `structure` (baris tak menutup semua grup — simpan menambah
 *     grup yang terputus, mig 212) → dicentang hanya bila ada baris AKTIF yang
 *     set opsinya bagian dari kombinasi itu — cermin pewaris superset
 *     `planVariantSave`, jadi niat lama "dijual" ikut menurun, dan tak lebih.
 *   · TERBUKA → dicentang (kecuali PO terbatas, ready stock).
 */
export function absentCheckedOnLoad(input: {
  product: ComboModeProduct;
  comboCount: number;
  rebuild: ComboRebuildReason;
  rows: readonly { option_ids: readonly string[]; is_active: boolean }[];
}): (optionIds: readonly string[]) => boolean {
  const { product, rebuild } = input;
  if (isComboListClosed(product)) {
    if (rebuild !== "structure") return () => false;
    const activeSets = input.rows
      .filter((r) => r.is_active && r.option_ids.length > 0)
      .map((r) => r.option_ids);
    return (optionIds) => {
      const have = new Set(optionIds);
      return activeSets.some((ids) => ids.every((id) => have.has(id)));
    };
  }
  if (rebuild !== "unmaterialized") return () => true;
  const sellable = rowlessComboSellable({ ...product, comboCount: input.comboCount });
  return () => sellable;
}

// ─── Picker ──────────────────────────────────────────────────────────────────

/**
 * Cara storefront (halaman produk, bundel) memuat pemilih varian:
 *   · `rows`       — dari baris yang terambil (daftar-izin implisit);
 *   · `lazy-deny`  — pohon opsi + daftar kombinasi MATI, sisanya lahir saat
 *                    dipesan (perilaku hari ini untuk preorder > 200);
 *   · `lazy-allow` — pohon opsi + daftar kombinasi yang DIJUAL (RPC
 *                    `preorder_sellable_combo_list`): produk TERTUTUP > 200.
 *
 * Tanpa tanda = persis hari ini (`shouldSkipInertVariantRows`).
 */
export type StorefrontComboSource = "rows" | "lazy-deny" | "lazy-allow";

export function storefrontComboSource(
  p: ComboModeProduct & { comboCount: number; fetchedCount: number }
): StorefrontComboSource {
  if (productTypeOf(p) !== "preorder") return "rows";
  const big = p.comboCount > LAZY_VARIANT_THRESHOLD;
  if (isComboListClosed(p)) return big ? "lazy-allow" : "rows";
  if (isComboListOpen(p)) {
    if (big) return "lazy-deny";
    // Terbuka ≤ 200 berlubang (opsi dikurangi dari > 200): kombinasi tanpa
    // baris tetap dijual, jadi picker baris saja akan menyembunyikannya.
    return p.fetchedCount < p.comboCount ? "lazy-deny" : "rows";
  }
  return big ? "lazy-deny" : "rows";
}

/**
 * Picker layar yang MEMEGANG seluruh baris (Edit Pesanan, keranjang admin,
 * Kelola PO) — malas (boleh melahirkan baris) atau dari baris saja?
 *
 * Tertutup → tidak pernah malas: baris aktif di tangan = daftar yang dijual.
 * Terbuka → malas bila ada kombinasi tanpa baris. Tanpa tanda = hari ini.
 */
export function pickerIsLazy(input: {
  product: ComboModeProduct;
  variantGroups: readonly { options: readonly unknown[] }[];
  materializedCount: number;
}): boolean {
  const { product } = input;
  if (isComboListClosed(product)) return false;
  if (isComboListOpen(product)) {
    if (productTypeOf(product) !== "preorder") return false;
    if (input.variantGroups.length === 0) return false;
    const total = deriveComboCount(input.variantGroups.map((g) => g.options.length));
    return total > input.materializedCount;
  }
  return variantPickerIsLazy({
    productType: productTypeOf(product),
    // `variantPickerIsLazy` hanya membaca `options.length`.
    variantGroups: input.variantGroups as Parameters<typeof variantPickerIsLazy>[0]["variantGroups"],
    materializedCount: input.materializedCount,
  });
}

/**
 * Himpunan "kombinasi mati" yang dibangun dari daftar-IZIN: setiap kombinasi
 * LENGKAP yang tak ada di daftar dianggap mati. Seluruh logika pemilih
 * (`isComboBlocked`, `resolveSelectionForOption`, `firstSelectableSelection`,
 * `isOptionFullyBlocked`) hanya memakai `.size` dan `.has` — jadi produk
 * tertutup memakai pemilih yang SAMA tanpa mengirim komplemennya (produk
 * terbesar PROD 14.430 kombinasi; komplemennya ±1,6 MB).
 *
 * Kunci yang bukan kombinasi lengkap tak pernah ditanyakan pemilih; bila
 * ditanyakan, jawabannya "mati" (arah aman).
 */
class AllowListBlockedSet implements BlockedComboKeys, Iterable<string> {
  private readonly allowed: ReadonlySet<string>;
  private readonly groups: readonly (readonly string[])[];
  readonly size: number;

  constructor(groups: readonly (readonly string[])[], sellable: BlockedCombos) {
    this.groups = groups;
    // Hanya kombinasi SAH pohon opsi sekarang (tepat satu opsi per grup) yang
    // dihitung: baris aktif ber-`option_ids` basi (opsinya sudah dihapus) tak
    // boleh membuat `size` jatuh ke 0 — `size` dipakai sebagai "ada yang
    // dikunci?", dan 0 berarti pemilih membuka SEMUA kombinasi.
    const groupOf = new Map<string, number>();
    groups.forEach((g, gi) => g.forEach((id) => groupOf.set(id, gi)));
    const valid = sellable.filter((ids) => {
      if (ids.length !== groups.length) return false;
      const seen = new Set<number>();
      for (const id of ids) {
        const gi = groupOf.get(id);
        if (gi === undefined || seen.has(gi)) return false;
        seen.add(gi);
      }
      return true;
    });
    this.allowed = buildBlockedComboSet(valid);
    const total = deriveComboCount(groups.map((g) => g.length));
    this.size = Math.max(0, total - this.allowed.size);
  }

  has(key: string): boolean {
    return !this.allowed.has(key);
  }

  *keys(): Generator<string> {
    let rows: string[][] = [[]];
    for (const g of this.groups) rows = rows.flatMap((r) => g.map((id) => [...r, id]));
    for (const r of rows) {
      if (r.length === 0) continue;
      const key = comboBlockKey(r);
      if (!this.allowed.has(key)) yield key;
    }
  }

  [Symbol.iterator](): Iterator<string> {
    return this.keys();
  }
}

/**
 * Himpunan kunci kombinasi yang TAK bisa dipilih pemilih storefront.
 * `sellableCombos` terisi (produk tertutup > 200) → komplemen daftar-izin;
 * selain itu daftar mati biasa (perilaku hari ini).
 */
export function pickerBlockedSet(input: {
  groups: readonly { options: readonly { id: string }[] }[];
  blockedCombos?: BlockedCombos | null;
  sellableCombos?: BlockedCombos | null;
}): BlockedComboKeys & Iterable<string> {
  if (input.sellableCombos) {
    return new AllowListBlockedSet(
      input.groups.map((g) => g.options.map((o) => o.id)),
      input.sellableCombos
    );
  }
  return buildBlockedComboSet(input.blockedCombos ?? []);
}

/**
 * Himpunan kunci kombinasi yang DIJUAL untuk keadaan (baris, tanda) tertentu —
 * dipakai uji invarian aturan 5 dan bukti "nol beda" sebelum/sesudah.
 */
export function sellableComboKeys(input: {
  product: ComboModeProduct;
  /** Seluruh kombinasi kartesius (option_ids). */
  combos: readonly (readonly string[])[];
  rows: readonly { option_ids: readonly string[]; is_active: boolean }[];
}): Set<string> {
  const byKey = new Map(input.rows.map((r) => [comboBlockKey(r.option_ids), r.is_active]));
  const rowless = rowlessComboSellable({ ...input.product, comboCount: input.combos.length });
  const out = new Set<string>();
  for (const combo of input.combos) {
    const key = comboBlockKey(combo);
    const active = byKey.get(key);
    if (active === undefined ? rowless : active) out.add(key);
  }
  return out;
}
